import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Trophy,
  AlertCircle,
  ChevronRight,
  Sparkles,
  Target
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
// import api from '../utils/api'
import lessonsData from '../data/lessons.json'
import toast from 'react-hot-toast'
import Footer from '../components/Footer'
import '../styles/quiz.css'

/**
 * QuizScreen Component
 * Interactive quiz to test knowledge after completing a lesson
 */
const QuizScreen = () => {
  const { lessonId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [lesson, setLesson] = useState(null)
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [selectedAnswer, setSelectedAnswer] = useState(null)
  const [showExplanation, setShowExplanation] = useState(false)
  const [score, setScore] = useState(0)
  const [answers, setAnswers] = useState([])
  const [quizComplete, setQuizComplete] = useState(false)
  const [loading, setLoading] = useState(true)

  // Mutation for updating quiz score
  const updateQuizScoreMutation = useMutation({
    mutationFn: ({ lessonId, xpEarned, quizScore }) => 
      api.progress.updateProgress(user.id, { lessonId, xpEarned, quizScore }),
    onSuccess: () => {
      // Invalidate progress queries to refresh data
      queryClient.invalidateQueries(['userProgress', user.id])
      queryClient.invalidateQueries(['userBadges', user.id])
    },
    onError: (error) => {
      console.error('Error updating quiz score:', error)
    }
  })

  useEffect(() => {
    // Find the lesson and its quiz data
    const moduleId = lessonId.split('-')[0]
    const lessons = lessonsData[moduleId] || []
    const currentLesson = lessons.find(l => l.id === lessonId)
    
    if (currentLesson && currentLesson.quiz) {
      setLesson(currentLesson)
    }
    setLoading(false)
  }, [lessonId])

  const renderShell = (content, options = { showHeader: false }) => (
    <div className="quiz-shell">
      <div className="landing-ambient" />

      {options.showHeader && (
        <header className="quiz-header">
          <div className="container">
            <motion.button
              className="back-button"
              onClick={() => navigate(`/lesson/${lessonId}`)}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <ArrowLeft size={20} />
              <span>Back to Lesson</span>
            </motion.button>

            <motion.div
              className="quiz-progress"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="quiz-progress-header">
                <Target size={18} />
                <span className="progress-text">
                  Question {currentQuestion + 1} of {questions.length}
                </span>
              </div>
              <div className="progress-bar">
                <motion.div
                  className="progress-fill"
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </motion.div>
          </div>
        </header>
      )}

      {content}

      <Footer />
    </div>
  )

  if (loading) {
    return renderShell(
      <div className="container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading quiz...</p>
        </div>
      </div>
    )
  }

  if (!lesson || !lesson.quiz) {
    return renderShell(
      <div className="container">
        <div className="error-state">
          <AlertCircle size={48} />
          <h2>No quiz available</h2>
          <p>This lesson doesn't have a quiz yet.</p>
          <button className="btn btn-primary" onClick={() => navigate(`/lesson/${lessonId}`)}>
            Back to Lesson
          </button>
        </div>
      </div>
    )
  }

  const { questions } = lesson.quiz
  const currentQ = questions[currentQuestion]

  const handleAnswerSelect = (index) => {
    if (!showExplanation) {
      setSelectedAnswer(index)
    }
  }

  const handleSubmitAnswer = () => {
    if (selectedAnswer === null) return

    setShowExplanation(true)
    const isCorrect = selectedAnswer === currentQ.correctIndex
    
    // Update score and record answer
    if (isCorrect) {
      setScore(score + 1)
      toast.success('Correct! Well done!')
    } else {
      toast.error('Not quite right, but you\'re learning!')
    }
    
    setAnswers([...answers, {
      questionIndex: currentQuestion,
      selectedAnswer,
      isCorrect
    }])
  }

  const handleNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1)
      setSelectedAnswer(null)
      setShowExplanation(false)
    } else {
      // Quiz complete
      setQuizComplete(true)
    }
  }

  const calculateXPReward = () => {
    const baseXP = lesson.xpReward || 50
    const percentCorrect = (score / questions.length) * 100
    
    if (percentCorrect === 100) return baseXP
    if (percentCorrect >= 80) return Math.floor(baseXP * 0.8)
    if (percentCorrect >= 60) return Math.floor(baseXP * 0.6)
    return Math.floor(baseXP * 0.4)
  }

  const handleCompleteQuiz = () => {
    const moduleId = lessonId.split('-')[0]
    const percentCorrect = Math.round((score / questions.length) * 100)
    const xpEarned = calculateXPReward()
    
    // Update quiz score in database
    updateQuizScoreMutation.mutate({
      lessonId: lesson.id,
      xpEarned,
      quizScore: percentCorrect
    })
    
    toast.success(`You earned ${xpEarned} XP!`)
    
    // Navigate back to module
    setTimeout(() => {
      navigate(`/module/${moduleId}`)
    }, 1500)
  }

  if (quizComplete) {
    const percentCorrect = Math.round((score / questions.length) * 100)
    const xpEarned = calculateXPReward()

    return renderShell(
      <div className="container">
        <motion.div
          className="quiz-complete"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className="complete-icon"
            initial={{ scale: 0 }}
            animate={{ scale: 1, rotate: [0, -10, 10, 0] }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <Trophy size={64} />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Quiz Complete!
          </motion.h1>

          <motion.div
            className="score-summary"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="score-circle">
              <span className="score-percent">{percentCorrect}%</span>
              <span className="score-label">Score</span>
            </div>

            <div className="score-details">
              <p className="correct-count">
                {score} out of {questions.length} correct
              </p>
              <p className="xp-earned">
                <Sparkles size={18} />
                +{xpEarned} XP earned
              </p>
            </div>
          </motion.div>

          <motion.div
            className="motivation-message"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            {percentCorrect === 100 && <p>Perfect score! You're a survival expert!</p>}
            {percentCorrect >= 80 && percentCorrect < 100 && <p>Great job! You've mastered this material!</p>}
            {percentCorrect >= 60 && percentCorrect < 80 && <p>Good effort! Keep practicing to improve!</p>}
            {percentCorrect < 60 && <p>Keep learning! Every bit of knowledge helps!</p>}
          </motion.div>

          <motion.div
            className="quiz-actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/lesson/${lessonId}`)}
            >
              Review Lesson
            </button>
            <button
              className="btn btn-primary"
              onClick={handleCompleteQuiz}
            >
              Continue
              <ChevronRight size={20} />
            </button>
          </motion.div>
        </motion.div>
      </div>
    )
  }

  return renderShell(
    <main className="container quiz-main">
      <motion.div
        className="quiz-content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <AnimatePresence mode="wait">
          <motion.h2
            key={`q-${currentQuestion}`}
            className="question-text"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {currentQ.question}
          </motion.h2>
        </AnimatePresence>

        <div className="answer-options">
          {currentQ.options.map((option, index) => (
            <motion.div
              key={index}
              className={`answer-option ${
                selectedAnswer === index ? 'selected' : ''
              } ${
                showExplanation && index === currentQ.correctIndex ? 'correct' : ''
              } ${
                showExplanation && selectedAnswer === index && index !== currentQ.correctIndex ? 'incorrect' : ''
              }`}
              onClick={() => handleAnswerSelect(index)}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={!showExplanation ? { scale: 1.01, y: -2 } : {}}
              whileTap={!showExplanation ? { scale: 0.99 } : {}}
            >
              <div className="option-indicator">
                {showExplanation && index === currentQ.correctIndex && (
                  <CheckCircle size={20} />
                )}
                {showExplanation && selectedAnswer === index && index !== currentQ.correctIndex && (
                  <XCircle size={20} />
                )}
                {(!showExplanation || (index !== currentQ.correctIndex && index !== selectedAnswer)) && (
                  <span className="option-letter">{String.fromCharCode(65 + index)}</span>
                )}
              </div>
              <span className="option-text">{option}</span>
            </motion.div>
          ))}
        </div>

        <AnimatePresence>
          {showExplanation && (
            <motion.div
              className="explanation-box"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <div className="explanation-header">
                <AlertCircle size={20} />
                <span>Explanation</span>
              </div>
              <p>{currentQ.explanation}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="quiz-actions">
          {!showExplanation ? (
            <motion.button
              className="btn btn-primary"
              onClick={handleSubmitAnswer}
              disabled={selectedAnswer === null}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Submit Answer
            </motion.button>
          ) : (
            <motion.button
              className="btn btn-primary"
              onClick={handleNextQuestion}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {currentQuestion < questions.length - 1 ? (
                <>
                  Next Question
                  <ChevronRight size={20} />
                </>
              ) : (
                'View Results'
              )}
            </motion.button>
          )}
        </div>
      </motion.div>
    </main>,
    { showHeader: true }
  )
}

export default QuizScreen
